import { Edit, FormDataConsumer, Labeled, minValue, ReferenceInput, SaveButton, SelectInput, SimpleForm, TextInput, Toolbar, UpdateWithConfirmButton, useDataProvider, useNotify, useRefresh } from "react-admin";
import { Permissions, PriceInput, RequiredChain, Tooltip } from "../../components";
import { PERMISSIONS } from '../../constants';
import { getChainId, getPlaceId } from "../../lib";
import { Box, SxProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from "react";
import { track, EVENTS } from '../../lib/analytics';

const containerSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  width: 300,
  marginTop: 40,
};

const FuelInput = ({ placeId, fuelId, label, placeFuels }) => {
  const placeIndex = placeFuels.findIndex(item => item.placeId === placeId);
  const fuelIndex = placeFuels[placeIndex].fuels.findIndex(item => item.fuelId === fuelId);

  return (
    <Labeled label={label}>
      <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title="Se o campo for deixado como zero, o valor não será exibido." >
        <PriceInput
          source={`placeFuels[${placeIndex}].fuels[${fuelIndex}].price`}
          label="Preço por litro"
          precision={3}
          locales="pt-BR"
        />
      </Tooltip>
    </Labeled>
  );
};

const StationFuels = ({ placeFuels, placeId }) => {
  const [fuels, setFuels] = useState([]);
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();

  useEffect(() => {
    setLoading(true);
    dataProvider.getList('fuels', { sort: { field: 'name', order: 'ASC' }, pagination: { page: 1, perPage: false }, filter: { placeId } })
      .then(({ data }) => {
        setFuels(data);
      }).finally(() => setLoading(false));
  }, [placeId]);

  if (loading) { return null; }

  return (
    fuels.map(({ id, name }) => (
      <FuelInput placeFuels={placeFuels} placeId={placeId} fuelId={id} label={name} />
    ))
  );
};

const FuelPrices = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();

  const onSuccess = () => {
    track(EVENTS.FUEL_PRICES_EDITED, {
      chain_id: getChainId() ?? 'unknown',
      place_id: getPlaceId() ?? 'all',
    });
    notify('ra.notification.updated', { messageArgs: { smart_count: 1 } });
    refresh();
  };

  const onError = (error) => {
    notify('ra.notification.data_provider_error', { type: 'error' });
    navigate('/fuel-prices-edit');
  };

  const transformPrices = values => {
    return (
      values.placeFuels.filter((pl) => pl?.placeId === values?.placeId).map(({ fuels, ...rest }) => ({
        ...rest, fuels: fuels.map(({ fuelId, placeId, price }) => ({ fuelId, placeId, price }))
      }))[0]
    );
  };

  const ToolbarCustom = () => {
    return (
      <Toolbar>
        <SaveButton label="Atualizar" />
      </Toolbar>
    );
  }

  return (
    <Permissions permissions={[PERMISSIONS.FUEL_PRICE]}>
      <RequiredChain>
        <Edit id={getChainId()} transform={transformPrices} title="Preço do Combustível" resource="fuel-prices" mutationMode="pessimistic" mutationOptions={{ onSuccess, onError }}>
          <SimpleForm toolbar={<ToolbarCustom />} defaultValues={{ placeId: getPlaceId() ?? null }} >
            <Box sx={containerSx}>
              {
                !getPlaceId() &&
                <ReferenceInput
                  filter={{ chainId: getChainId(), type: 'station' }}
                  sort={{ field: 'name', order: 'ASC' }}
                  source="placeId"
                  reference="places"
                  perPage={null}
                >
                  <SelectInput
                    label="Local"
                    optionText="name"
                    emptyText="ra.action.clear_input_value"
                  />
                </ReferenceInput>
              }
              <FormDataConsumer>
                {({ formData: { placeId, placeFuels } }) =>
                  placeId && (<StationFuels placeFuels={placeFuels} placeId={placeId} />)
                }
              </FormDataConsumer>
            </Box>
          </SimpleForm>
        </Edit>
      </RequiredChain>
    </Permissions>
  );
}

export default FuelPrices;